import React from 'react';
import { motion } from 'framer-motion';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // In production, you might want to log to an error reporting service
    // logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    const { retryCount } = this.state;
    const maxRetries = 3;
    
    if (retryCount < maxRetries) {
      this.setState({ 
        hasError: false, 
        error: null, 
        errorInfo: null,
        retryCount: retryCount + 1 
      });
    } else {
      // After max retries, reload the page
      window.location.reload();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { retryCount } = this.state;
      const maxRetries = 3;
      
      // Custom fallback UI
      return (
        <motion.div 
          className="error-boundary"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="error-boundary-content">
            <div className="error-icon">
              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
            </div>
            <h2>Oops! Something went wrong</h2>
            <p>
              We're sorry, but something unexpected happened. 
              {retryCount > 0 && (
                <span> This is attempt {retryCount + 1} of {maxRetries + 1}.</span>
              )}
            </p>
            
            <div className="error-actions">
              {retryCount < maxRetries ? (
                <button 
                  className="btn btn-primary" 
                  onClick={this.handleRetry}
                  aria-label="Try again"
                >
                  <i className="fas fa-redo" aria-hidden="true"></i>
                  Try Again
                </button>
              ) : (
                <button 
                  className="btn btn-primary" 
                  onClick={this.handleReload}
                  aria-label="Reload page"
                >
                  <i className="fas fa-sync" aria-hidden="true"></i>
                  Reload Page
                </button>
              )}
              
              <a 
                href="mailto:info@splitnest.org" 
                className="btn btn-secondary"
                aria-label="Contact support"
              >
                <i className="fas fa-envelope" aria-hidden="true"></i>
                Contact Support
              </a>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Error Details (Development Only)</summary>
                <pre className="error-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </motion.div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;